import React, { useEffect, useContext, createContext, useState, useCallback, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useHint, useGuidance } from "./T2TGuidanceProvider";

// ---- Hint Queue — only one hint visible at a time ----
const HintQueueContext = createContext(null);

export const HintQueueProvider = ({ children }) => {
  const [activeHintId, setActiveHintId] = useState(null);
  const [registeredHints, setRegisteredHints] = useState([]);

  const register = useCallback((hintId) => {
    setRegisteredHints((prev) => prev.includes(hintId) ? prev : [...prev, hintId]);
  }, []);

  const unregister = useCallback((hintId) => {
    setRegisteredHints((prev) => prev.filter((h) => h !== hintId));
  }, []);

  const promoteNext = useCallback((dismissedId) => {
    setRegisteredHints((prev) => {
      const remaining = prev.filter((h) => h !== dismissedId);
      setActiveHintId(remaining.length > 0 ? remaining[0] : null);
      return remaining;
    });
  }, []);

  // Set first registered hint as active if none active
  useEffect(() => {
    if (!activeHintId && registeredHints.length > 0) {
      setActiveHintId(registeredHints[0]);
    }
  }, [registeredHints, activeHintId]);

  return (
    <HintQueueContext.Provider value={{ activeHintId, register, unregister, promoteNext }}>
      {children}
    </HintQueueContext.Provider>
  );
};

HintQueueProvider.propTypes = { children: PropTypes.node };

/**
 * All 18 contextual hint definitions — 4 categories
 * Each hint: { id, title, body, example }
 */
export const HINT_DEFS = {
  // ---- Canvas hints (6) ----
  "canvas.welcome": {
    title: "This is your workflow canvas",
    body: "Drag stages here to build your approval flow. Think of each stage as one step in the process.",
    example: "e.g. \u2018Finance Review\u2019 is a stage where your finance team checks the numbers.",
  },
  "canvas.add_stage": {
    title: "Click here to add a step",
    body: "Each step in your process is called a stage. Drag one from the palette to the canvas for each person or team that reviews the request.",
    example: "e.g. Add stages for: Submission \u2192 Manager Review \u2192 Finance \u2192 Final Approval",
  },
  "canvas.first_stage": {
    title: "Name this stage clearly",
    body: "Double-click the stage to rename it. Use the name of the action \u2014 not the person.",
    example: "Good: \u2018Finance Review\u2019. Avoid: \u2018John\u2019s Step\u2019",
  },
  "canvas.draw_arrow": {
    title: "Connect stages with arrows",
    body: "Click the small circle (\u25B6) at a stage\u2019s right edge, then click the circle on the next stage\u2019s left edge. Arrows show which step comes next.",
    example: "Hover over a stage edge \u2192 dot appears \u2192 click and then click the target stage.",
  },
  "canvas.end_stage": {
    title: "Every workflow needs an End",
    body: "The End stage is the finish line. When a request reaches End, it is complete and the system acts on it.",
    example: "Without an End stage, requests get stuck at the last stage forever.",
  },
  "canvas.orphaned_stage": {
    title: "This stage is disconnected",
    body: "This stage has no arrows connecting it. Every stage needs at least one arrow coming in and one going out (except the first and last).",
    example: "Fix: draw an arrow from the previous stage to this one.",
  },

  // ---- Condition builder hints (5) ----
  "conditions.intro": {
    title: "Conditions control which path",
    body: "Without a condition, every request follows the same path. Add a condition when the routing should change based on form data.",
    example: "\u2018If amount > 1,00,000 \u2192 go to CFO. Otherwise \u2192 go to Finance.\u2019",
  },
  "conditions.field": {
    title: "Choose what to check",
    body: "Pick the form field that determines the routing. The workflow will look at this value when deciding where to send the request.",
    example: "If routing by value: choose Amount. By type: choose Request Category.",
  },
  "conditions.operator": {
    title: "Choose the comparison",
    body: "\u2018More than\u2019 means the value must exceed your number. \u2018Equals\u2019 means an exact match. \u2018Is one of\u2019 lets you list multiple values.",
    example: "\u2018Amount is more than 50000\u2019 = any request above \u20B950,000 takes this path.",
  },
  "conditions.default": {
    title: "Set a default path",
    body: "The default path is where requests go when no condition matches. Always set a default \u2014 otherwise requests with unexpected values get stuck.",
    example: "Without a default: a request with an unusual value has no path. It stops.",
  },
  "conditions.preview": {
    title: "Test your condition",
    body: "Use the Test button to enter sample values and see which path your condition sends them to. Test before publishing.",
    example: "Enter amount = 75000 \u2192 should go to Finance. Enter 200000 \u2192 should go to CFO.",
  },

  // ---- Role assignment hints (4) ----
  "roles.intro": {
    title: "Roles control who can act here",
    body: "A role is a job function \u2014 like Finance Controller or Procurement Manager. Everyone with that role can approve at this stage.",
    example: "Add \u2018Finance Controller\u2019 as the role. Then assign your 3 finance team members to it.",
  },
  "roles.no_role": {
    title: "This stage has no role",
    body: "Without a role assigned, no one can approve requests at this stage \u2014 they will get stuck here.",
    example: "Click this stage \u2192 Roles \u2192 assign who should approve.",
  },
  "roles.multi_role": {
    title: "Multiple roles can share a stage",
    body: "When multiple roles are on one stage, any person with any of those roles can act on the request. First person to act wins.",
    example: "Use this for \u2018anyone from Finance\u2019 \u2014 add all finance roles.",
  },

  // ---- Publish hints (4) ----
  "publish.health": {
    title: "Fix issues before you go live",
    body: "These are things the system found that would cause problems. Fix them before publishing \u2014 you cannot publish until all blocking issues are resolved.",
    example: "\u2018No End stage\u2019 = every request gets stuck. Fix: add an End stage.",
  },
  "publish.action": {
    title: "Publishing makes this live",
    body: "Once published, your team can immediately start submitting requests. You can still edit the workflow at any time \u2014 it just creates a new version.",
    example: "Think of versions like saved game checkpoints. You can always go back.",
  },
  "publish.versioning": {
    title: "Every change creates a new version",
    body: "When you edit and republish, version 2 is created. Requests on version 1 finish on version 1. New requests use version 2.",
    example: "Safe to edit: in-flight requests are never affected by your changes.",
  },
  "publish.history": {
    title: "See every version published",
    body: "The version history shows every time you published this workflow. You can restore any old version as a draft and republish it.",
    example: "Accidentally broke something? Restore version 1 and republish \u2014 2 clicks.",
  },
};

/**
 * Hint — inline component that renders a hint tooltip if not dismissed.
 * Wraps the target element. Hint appears relative to the wrapper.
 *
 * Usage:
 *   <Hint id="canvas.add_stage" position="bottom">
 *     <button>+ Add Stage</button>
 *   </Hint>
 */
export const Hint = ({ id, position = "bottom", children, style }) => {
  const { shouldShow, dismiss } = useHint(id);
  const { hintsEnabled } = useGuidance();
  const queue = useContext(HintQueueContext);
  const def = HINT_DEFS[id];
  const wrapperRef = useRef(null);

  // Register/unregister in queue
  useEffect(() => {
    if (shouldShow && hintsEnabled && queue) {
      queue.register(id);
      return () => queue.unregister(id);
    }
  }, [shouldShow, hintsEnabled, id, queue]);

  if (!def) return children || null;

  // Only show if this hint is the currently active one in the queue
  const isActive = queue ? queue.activeHintId === id : true;
  const visible = shouldShow && hintsEnabled && isActive;

  const handleDismiss = () => {
    dismiss();
    if (queue) queue.promoteNext(id);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative", display: "inline-block", ...style }}>
      {children}
      {visible && (
        <HintBubble
          title={def.title}
          body={def.body}
          example={def.example}
          position={position}
          targetRef={wrapperRef}
          onDismiss={handleDismiss}
        />
      )}
    </div>
  );
};

Hint.propTypes = {
  id: PropTypes.string.isRequired,
  position: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  children: PropTypes.node,
  style: PropTypes.object,
};

/**
 * HintBubble — the actual tooltip bubble.
 * Rendered via portal to document.body with viewport-clamped fixed positioning
 * so it escapes any overflow:hidden ancestor (canvas body, toolbar, etc.) and
 * never runs off the visible viewport.
 */
const HintBubble = ({ title, body, example, position, targetRef, onDismiss }) => {
  const bubbleRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, visibility: "hidden" });

  useLayoutEffect(() => {
    const compute = () => {
      const target = targetRef?.current;
      const bubble = bubbleRef.current;
      if (!target || !bubble) return;
      const tr = target.getBoundingClientRect();
      const br = bubble.getBoundingClientRect();
      const gap = 8;
      const pad = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let top, left;
      if (position === "top") {
        top  = tr.top - br.height - gap;
        left = tr.left + tr.width / 2 - br.width / 2;
      } else if (position === "left") {
        top  = tr.top + tr.height / 2 - br.height / 2;
        left = tr.left - br.width - gap;
      } else if (position === "right") {
        top  = tr.top + tr.height / 2 - br.height / 2;
        left = tr.right + gap;
      } else {
        top  = tr.bottom + gap;
        left = tr.left + tr.width / 2 - br.width / 2;
      }
      left = Math.max(pad, Math.min(left, vw - br.width - pad));
      top  = Math.max(pad, Math.min(top,  vh - br.height - pad));
      setCoords({ top, left, visibility: "visible" });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [position, title, body, targetRef]);

  return createPortal(
    <div
      ref={bubbleRef}
      className="t2t-hint-tooltip"
      style={{ position: "fixed", top: coords.top, left: coords.left, visibility: coords.visibility }}
    >
      <div className="t2t-hint-header">
        <span className="t2t-hint-title">{title}</span>
        <button className="t2t-hint-dismiss" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDismiss(); }} title="Got it">✕</button>
      </div>
      <div className="t2t-hint-body">{body}</div>
      {example && (
        <div className="t2t-hint-example">
          <span style={{ fontWeight: 600 }}>Example: </span>{example}
        </div>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDismiss(); }}
        style={{ marginTop: 8, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 4, color: "white", fontSize: 10, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}
      >Got it</button>
    </div>,
    document.body
  );
};

HintBubble.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  example: PropTypes.string,
  position: PropTypes.string,
  targetRef: PropTypes.object,
  onDismiss: PropTypes.func.isRequired,
};
